import { randomBytes } from "node:crypto";
import type {
  BillingProviderConfigMap,
  MollieBillingProviderConfig,
} from "../../../providers/types.ts";
import type { BillingSqlExecutor } from "../../../subject/repository.ts";
import { resolveBillingEnvironment } from "../../environment.ts";
import {
  isBillingWebhookIngressBindingToken,
  resolveBillingIngressEndpoints,
} from "../../../ingestion/urls.ts";
import { defaultBillingCredentialReference } from "../providers/connection-binding.ts";
import {
  type BillingConnectionIdentity,
  deriveConfiguredProviderAccountReference,
} from "./identity.ts";

export interface MaterializedBillingConnection {
  accountReference: string;
  classicWebhookUrl?: string;
  credentialReference: string;
  eventsWebhookUrl?: string;
  id: string;
  provider: "mollie";
  webhookIngressToken: string;
}

export interface MaterializeConfiguredBillingConnectionsResult {
  connections: readonly MaterializedBillingConnection[];
}

export interface ConfiguredBillingConnectionIntent {
  accountReference: string;
  credentialKind: string;
  credentialReference: string;
  environment: "test" | "live";
  identity: BillingConnectionIdentity;
  provider: "mollie";
  /**
   * Provider-confirmed remote account identity (e.g. a Mollie organization
   * id returned by discovery). Null until verification — never invent one
   * from config. `accountReference` stays the local upsert identity.
   */
  providerAccountId: string | null;
}

function mollieCredentialKind(config: MollieBillingProviderConfig): string {
  if (config.credentialKind != null) {
    return config.credentialKind;
  }
  if ("accessToken" in config) {
    return "advanced_access_token";
  }
  return "api_key";
}

export function configuredBillingConnectionIntents(input: {
  applicationId: string;
  configuredProviders?: BillingProviderConfigMap;
  provider?: "mollie";
  testMode?: boolean;
}): readonly ConfiguredBillingConnectionIntent[] {
  const applicationId = input.applicationId.trim();
  if (applicationId.length === 0) {
    return [];
  }
  const environment = resolveBillingEnvironment({
    testMode: input.testMode,
  }).name;
  const intents: ConfiguredBillingConnectionIntent[] = [];
  const includeDefaultMollie =
    !Object.hasOwn(input, "provider") ||
    input.provider == null ||
    input.provider === "mollie";
  const mollie = input.configuredProviders?.mollie;
  if (mollie != null && includeDefaultMollie) {
    const mollieConfig = mollie;
    const credentialReference = defaultBillingCredentialReference(
      "mollie",
      environment
    );
    const accountReference = deriveConfiguredProviderAccountReference({
      applicationId,
      environment,
      provider: "mollie",
      scope: "organization",
    });
    intents.push({
      accountReference,
      credentialKind: mollieCredentialKind(mollieConfig),
      credentialReference,
      environment,
      identity: {
        credentialReference,
        environment,
        provider: "mollie",
      },
      provider: "mollie",
      providerAccountId: null,
    });
  }
  const accounts = input.configuredProviders?.mollieAccounts;
  if (accounts != null) {
    for (const [accountId, config] of Object.entries(accounts)) {
      if (config == null) {
        continue;
      }
      const slot = accountId.trim();
      if (slot.length === 0) {
        continue;
      }
      const accountReference = deriveConfiguredProviderAccountReference({
        applicationId,
        environment,
        provider: "mollie",
        scope: slot,
      });
      const credentialReference = `providers.mollie:${slot}`;
      intents.push({
        accountReference,
        credentialKind: mollieCredentialKind(config),
        credentialReference,
        environment,
        identity: {
          credentialReference,
          environment,
          provider: "mollie",
          scopeKey: slot,
        },
        provider: "mollie",
        providerAccountId: null,
      });
    }
  }
  return intents;
}

const ENSURE_DECLARED_SLOT_INDEX_SQL = `
CREATE UNIQUE INDEX IF NOT EXISTS idx_billing_provider_connections_declared_slot
ON billing.billing_provider_connections (
	owner_id,
	provider,
	environment,
	credential_reference
)
WHERE deleted_at IS NULL
`;

const billingConnectionDeclaredSlotSql = ENSURE_DECLARED_SLOT_INDEX_SQL;

function sqlTextLiteral(value: string, label: string): string {
  if (value.length === 0 || value.length > 256) {
    throw new Error(
      `Configured billing connection ${label} is empty or too long.`
    );
  }
  if (!/^[A-Za-z0-9._:+-]+$/.test(value)) {
    throw new Error(
      `Configured billing connection ${label} must not contain SQL metacharacters.`
    );
  }
  return `'${value}'`;
}

function sqlNullableProviderAccountId(value: string | null): string {
  if (value == null) {
    return "NULL";
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error(
      "Configured billing connection provider_account_id must be null or non-empty."
    );
  }
  return sqlTextLiteral(trimmed, "provider_account_id");
}

const CONFIGURED_CONNECTION_INIT = Symbol.for(
  "@xylex-group/athena.billingConfiguredConnectionInit"
);

type ConfiguredConnectionInitCache = Map<
  string,
  Promise<MaterializeConfiguredBillingConnectionsResult>
>;

function configuredConnectionInitCache(): ConfiguredConnectionInitCache {
  const holder = globalThis as typeof globalThis & {
    [CONFIGURED_CONNECTION_INIT]?: ConfiguredConnectionInitCache;
  };
  holder[CONFIGURED_CONNECTION_INIT] ??= new Map();
  return holder[CONFIGURED_CONNECTION_INIT];
}

export function configuredBillingConnectionInitKey(input: {
  applicationId: string;
  provider?: "mollie";
  testMode?: boolean;
}): string {
  const environment = resolveBillingEnvironment({
    testMode: input.testMode,
  }).name;
  return `${input.applicationId.trim()}\0${environment}\0${input.provider ?? "*"}`;
}

/**
 * One initialization promise per process runtime. Concurrent callers await
 * the same promise. Failed bootstrap is cached so capabilities/sign-in
 * probes do not rematerialize (retry is deliberate — admin rematerialize).
 */
export function singleFlightConfiguredBillingConnectionMaterialize(
  key: string,
  run: () => Promise<MaterializeConfiguredBillingConnectionsResult>
): Promise<MaterializeConfiguredBillingConnectionsResult> {
  const cache = configuredConnectionInitCache();
  const existing = cache.get(key);
  if (existing) {
    return existing;
  }
  const started = run();
  cache.set(key, started);
  return started;
}

function upsertConfiguredConnectionSql(input: {
  accountReference: string;
  applicationId: string;
  credentialKind: string;
  credentialReference: string;
  environment: "test" | "live";
  provider: "mollie";
  providerAccountId: string | null;
  webhookIngressToken: string;
}): string {
  const applicationId = sqlTextLiteral(input.applicationId, "owner_id");
  const provider = sqlTextLiteral(input.provider, "provider");
  const environment = sqlTextLiteral(input.environment, "environment");
  const credentialKind = sqlTextLiteral(
    input.credentialKind,
    "credential_kind"
  );
  const credentialReference = sqlTextLiteral(
    input.credentialReference,
    "credential_reference"
  );
  const accountReference = sqlTextLiteral(
    input.accountReference,
    "account_reference"
  );
  const webhookIngressToken = sqlTextLiteral(
    input.webhookIngressToken,
    "webhook_ingress_token"
  );
  const providerAccountId = sqlNullableProviderAccountId(
    input.providerAccountId
  );
  return `
INSERT INTO billing.billing_provider_connections (
	owner_kind,
	owner_id,
	provider,
	mode,
	environment,
	status,
	credential_kind,
	credential_reference,
	provider_account_id,
	account_reference,
	scopes,
	config,
	metadata
)
VALUES (
	'tenant',
	${applicationId},
	${provider},
	${environment},
	${environment},
	'active',
	${credentialKind},
	${credentialReference},
	${providerAccountId},
	${accountReference},
	'[]'::jsonb,
	'{}'::jsonb,
	jsonb_build_object(
		'managedBy', 'application_config',
		'webhookIngressToken', ${webhookIngressToken}
	)
)
ON CONFLICT (owner_kind, owner_id, provider, environment, credential_reference)
WHERE deleted_at IS NULL
DO UPDATE SET
	status = 'active',
	credential_kind = EXCLUDED.credential_kind,
	credential_reference = EXCLUDED.credential_reference,
	mode = EXCLUDED.mode,
	environment = EXCLUDED.environment,
	provider_account_id = COALESCE(
		EXCLUDED.provider_account_id,
		billing.billing_provider_connections.provider_account_id
	),
	account_reference = EXCLUDED.account_reference,
	scopes = EXCLUDED.scopes,
	config = EXCLUDED.config,
	metadata = COALESCE(billing.billing_provider_connections.metadata, '{}'::jsonb)
		|| jsonb_build_object('managedBy', 'application_config')
		|| jsonb_build_object(
			'webhookIngressToken',
			COALESCE(
				NULLIF(
					billing.billing_provider_connections.metadata->>'webhookIngressToken',
					''
				),
				EXCLUDED.metadata->>'webhookIngressToken'
			)
		),
	updated_at = now()
RETURNING id::text AS id,
	metadata->>'webhookIngressToken' AS webhook_ingress_token
`;
}

function supersedeOtherEnvironmentSql(input: {
  applicationId: string;
  credentialReference: string;
  environment: "test" | "live";
  id: string;
  provider: "mollie";
}): string {
  const applicationId = sqlTextLiteral(input.applicationId, "owner_id");
  const provider = sqlTextLiteral(input.provider, "provider");
  const credentialReference = sqlTextLiteral(
    input.credentialReference,
    "credential_reference"
  );
  const environment = sqlTextLiteral(input.environment, "environment");
  const id = sqlTextLiteral(input.id, "id");
  return `
UPDATE billing.billing_provider_connections
SET status = 'disabled',
	updated_at = now(),
	metadata = COALESCE(metadata, '{}'::jsonb)
		|| jsonb_build_object(
			'managedBy', 'application_config',
			'supersededByEnvironment', ${environment},
			'supersededByCredentialReference', ${credentialReference}
		)
WHERE deleted_at IS NULL
  AND status = 'active'
  AND owner_kind = 'tenant'
  AND owner_id = ${applicationId}
  AND lower(provider) = lower(${provider})
  AND id::text <> ${id}
  AND (
    credential_reference = ${credentialReference}
    OR (
      position(':' in credential_reference) = 0
      AND environment <> ${environment}
    )
  )
`;
}

function mintWebhookIngressToken(): string {
  return randomBytes(24).toString("base64url");
}

function assertNoSecretMaterial(value: unknown, label: string): void {
  if (typeof value !== "string") {
    return;
  }
  const lowered = value.toLowerCase();
  if (
    lowered.startsWith("access_") ||
    lowered.startsWith("live_") ||
    lowered.startsWith("test_") ||
    lowered.startsWith("sk_") ||
    lowered.includes("secret")
  ) {
    throw new Error(
      `Configured billing connection ${label} must not contain credential material.`
    );
  }
}

export async function materializeConfiguredBillingConnections(input: {
  applicationId: string;
  appUrl?: string | null;
  configuredProviders?: BillingProviderConfigMap;
  publicBaseUrl?: string | null;
  provider?: "mollie";
  sql: BillingSqlExecutor;
  testMode?: boolean;
}): Promise<MaterializeConfiguredBillingConnectionsResult> {
  const applicationId = input.applicationId.trim();
  const testMode = resolveBillingEnvironment({
    testMode: input.testMode,
  }).testMode;
  const intents = configuredBillingConnectionIntents({
    applicationId,
    configuredProviders: input.configuredProviders,
    provider: input.provider,
    testMode,
  });
  for (const intent of intents) {
    assertNoSecretMaterial(intent.credentialReference, "credential_reference");
    assertNoSecretMaterial(intent.accountReference, "account_reference");
  }
  const connections: MaterializedBillingConnection[] = [];
  if (intents.length > 0) {
    const duplicates = await input.sql.query(
      `SELECT owner_kind, owner_id, provider, environment, credential_reference, COUNT(*)::int AS n
			 FROM billing.billing_provider_connections
			 WHERE deleted_at IS NULL
			 GROUP BY owner_kind, owner_id, provider, environment, credential_reference
			 HAVING COUNT(*) > 1
			 LIMIT 1`
    );
    if (duplicates.rows[0]) {
      throw new Error(
        "ATHENA_BILLING: duplicate owner-slot connections exist; run an explicit remediation migration before materialize."
      );
    }
    try {
      await input.sql.query(billingConnectionDeclaredSlotSql);
    } catch {
      // Upsert targets idx_billing_provider_connections_identity from 0003.
    }
  }
  for (const intent of intents) {
    if (
      intent.providerAccountId !== null &&
      intent.providerAccountId.length > 0
    ) {
      const collision = await input.sql.query(
        `SELECT owner_id
				 FROM billing.billing_provider_connections
				 WHERE provider_account_id = $1
				   AND deleted_at IS NULL
				   AND owner_id <> $2
				 LIMIT 1`,
        [intent.providerAccountId, applicationId]
      );
      if (collision.rows[0]) {
        throw new Error(
          "Provider account is already bound to another billing owner."
        );
      }
    }
    const mintedWebhookIngressToken = mintWebhookIngressToken();
    assertNoSecretMaterial(mintedWebhookIngressToken, "webhook_ingress_token");
    const result = await input.sql.query(
      upsertConfiguredConnectionSql({
        accountReference: intent.accountReference,
        applicationId,
        credentialKind: intent.credentialKind,
        credentialReference: intent.credentialReference,
        environment: intent.environment,
        provider: intent.provider,
        providerAccountId: intent.providerAccountId,
        webhookIngressToken: mintedWebhookIngressToken,
      })
    );
    const row = result.rows[0];
    const id = row?.id;
    const webhookIngressToken = row?.webhook_ingress_token;
    if (typeof id !== "string" || id.length === 0) {
      throw new Error(
        "Configured billing connection materialization did not return an id."
      );
    }
    if (
      typeof webhookIngressToken !== "string" ||
      webhookIngressToken.length === 0
    ) {
      throw new Error(
        "Configured billing connection materialization did not return a webhook ingress token."
      );
    }
    assertNoSecretMaterial(webhookIngressToken, "webhook_ingress_token");
    if (!isBillingWebhookIngressBindingToken(webhookIngressToken)) {
      throw new Error(
        "Configured billing connection materialization returned an invalid webhook ingress token."
      );
    }
    const ingressEndpoints = resolveBillingIngressEndpoints({
      appUrl: input.appUrl,
      publicBaseUrl: input.publicBaseUrl,
      webhookIngressToken,
    });
    await input.sql.query(
      supersedeOtherEnvironmentSql({
        applicationId,
        credentialReference: intent.credentialReference,
        environment: intent.environment,
        id,
        provider: intent.provider,
      })
    );
    connections.push({
      accountReference: intent.accountReference,
      ...(ingressEndpoints
        ? {
            classicWebhookUrl: ingressEndpoints.classicUrl,
            eventsWebhookUrl: ingressEndpoints.eventsUrl,
          }
        : {}),
      credentialReference: intent.credentialReference,
      id,
      provider: intent.provider,
      webhookIngressToken,
    });
  }
  return { connections };
}

/** Declared `billing.providers` converge here; apps must not call createConnection(). */
export const DECLARED_BILLING_PROVIDER_MATERIALIZES_WITHOUT_CREATE_CONNECTION =
  true as const;
